import { useHistory, useParams } from "react-router-dom";
import { TimeFinderOption } from "../../../../../../src/api/trpc/time-finder";
import { AppTimeFinder } from "../../../../time-finder/components/time_finder";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { DateTime } from "luxon";
import { common_utils } from "../../../../../../common/common_utils";
import { useCurrentGuildData } from "../hooks/use_guild_data";
import { DashboardUtils } from "../../../dashboard_utils";
import { useRoles } from "../hooks/use_roles";
import { useChannelName } from "../hooks/use_channel_name";

export function TimeFinderEdit(props: { time_finder_id: string }) {
	const history = useHistory();
	const { server_id } = useParams<{ server_id: string }>();

	const mutation = trpc.time_finder.updateTimeFinder.useMutation({
		onSuccess: () => {
			history.push(
				common_utils.getTimeFinderURL({
					server_id: server_id,
					type: "view",
					instance_id: props.time_finder_id,
				})
			);
		},
	});

	const time_finder_query = trpc.time_finder.getById.useQuery({
		id: props.time_finder_id,
		guild_id: server_id,
	});

	const does_have_permission_query =
		trpc.members.doesUserHavePermission.useQuery(
			{
				guild_id: server_id,
				permission: "create",
			},
			{
				onSuccess: (val) => {},
			}
		);

	const guild = useCurrentGuildData();
	const roles = useRoles();
	
	// Get channel name for the edit display - call before any conditional returns
	const channelName = useChannelName(
		time_finder_query.data?.channel_id || "",
		true // include_hashtag
	);

	if (
		does_have_permission_query.isSuccess &&
		!does_have_permission_query.data
	) {
		return (
			<div className="flex w-full  flex-col items-center">
				<DashboardUtils.RenderErrorContent
					text="You must have 'create' permissions in order to create a time finder."
					title="Invalid Permissions"
				/>
			</div>
		);
	}

	if (!time_finder_query.isSuccess) {
		return <LoadingThrobber />;
	}

	// Convert the server datetime format to the format expected by AppTimeFinder
	const convertToTimeFinderOption = (): TimeFinderOption[] => {
		const dateMap = new Map<string, TimeFinderOption>();

		time_finder_query.data.datetimes.forEach((datetime) => {
			const dt = DateTime.fromMillis(datetime.start_time).toUTC();
			const dateKey = `${dt.year}-${dt.month}-${dt.day}`;

			if (!dateMap.has(dateKey)) {
				dateMap.set(dateKey, {
					date: {
						day: dt.day,
						month: dt.month,
						year: dt.year,
					},
					timeslots: [],
				});
			}

			// If this has end times, add them to timeslots
			if (time_finder_query.data.are_times_specified) {
				const startOfDay = dt.startOf("day");

				dateMap.get(dateKey).timeslots.push({
					start_time: datetime.start_time,
					end_time: datetime.end_time,
				});
			}
		});

		return Array.from(dateMap.values());
	};

	const timeFinderOptions = convertToTimeFinderOption();

	return (
		<AppTimeFinder
			onSubmit={(data) => {
				mutation.mutate({
					id: props.time_finder_id,
					guild_id: server_id,
					title: data.title,
					description: data.description,
					selected_dates: data.selected_dates,
					are_times_specified: data.are_times_specified,
					is_public: data.is_public,
					anonymous_voting: data.anonymous_voting,
					hide_votes: data.hide_votes,
					eligible_role_ids: data.eligible_role_ids,
				user_mentions_on_create: data.user_mentions_on_create,
				role_mentions_on_create: data.role_mentions_on_create,
				});
			}}
			is_loading={mutation.isLoading}
			is_public={time_finder_query.data.is_public}
			is_on_dashboard={true}
			does_have_premium={guild?.is_premium}
			roles={roles}
			is_edit={true}
			initial_data={{
				title: time_finder_query.data.title,
				description: time_finder_query.data.description,
				selected_dates: timeFinderOptions,
				are_times_specified: time_finder_query.data.are_times_specified,
				anonymous_voting: time_finder_query.data.anonymous_voting,
				hide_votes: time_finder_query.data.hide_votes,
				eligible_role_ids:
					time_finder_query.data.eligible_role_ids || [],
				user_mentions_on_create:
					time_finder_query.data.user_mentions_on_create || [],
				role_mentions_on_create:
					time_finder_query.data.role_mentions_on_create || [],
				channel_id: time_finder_query.data.channel_id,
				channel_name: channelName,
			}}
		/>
	);
}
